import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { useSession } from '../store/session.js';
import { Badge, Banner, Button, Card, Field, Spinner } from '../components/ui.js';
import { IconImage, IconRuler, IconTarget } from '../components/icons.js';

interface Zone {
  zoneId: string;
  name: string;
  viewCode: string;
  polygonMm: Array<{ x: number; y: number }>;
  allowedTechniques: string[];
  provenance: 'inherited' | 'adjusted' | 'exception';
  confidence: number;
  size: string;
}

interface ProductDetail {
  id: string;
  code: string;
  name: string;
  description: string;
  material: string;
  substrate: string;
  customizable: boolean;
  thumbnailUrl: string | null;
  colors: Array<{
    id: string;
    name: string;
    approximateRgb: { r: number; g: number; b: number };
    dark: boolean;
    imagesByView: Record<string, string | null>;
    secondaryHex: string | null;
  }>;
  variants: Array<{ id: string; sku: string; colorId: string; size: string; available: boolean }>;
  zones: Zone[];
}

const TECHNIQUE_NAMES: Record<string, string> = {
  dtf: 'DTF',
  screen_print: 'Serigrafia',
  embroidery: 'Bordado',
  sublimation: 'Sublimacion',
};

export function ProductPage() {
  const { id = '' } = useParams();
  const t = useT();
  const navigate = useNavigate();
  const { user, login, entrarComoInvitado } = useSession();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [size, setSize] = useState<string>('');
  const [colorId, setColorId] = useState<string>('');
  const [zoneId, setZoneId] = useState<string>('');
  const [technique, setTechnique] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Cada peticion lleva su numero de secuencia. Cambiar de talla vuelve a pedir
   * las zonas (escalan con la talla), y sin esto la respuesta de una talla
   * anterior puede llegar DESPUES de la nueva y repintar la zona equivocada.
   */
  const peticion = useRef(0);

  // Espejo de lo que el cliente tiene elegido AHORA. El efecto no puede
  // depender de estos valores (volveria a pedir la ficha al tocar la zona),
  // pero si necesita leerlos para no perder la seleccion al cambiar de talla.
  const eleccion = useRef({ zoneId, technique });
  eleccion.current = { zoneId, technique };

  useEffect(() => {
    const mia = ++peticion.current;
    setRecalculando(true);
    api
      .getProduct(id, size ? { size } : {})
      .then((detail) => {
        if (mia !== peticion.current) return; // llego tarde: se descarta
        const p = detail as unknown as ProductDetail;
        setProduct(p);
        // Un fallo anterior NO puede quedarse pegado: si esta carga fue bien, la
        // ficha esta bien. Antes el error nunca se limpiaba y sustituia a la
        // pagina entera, asi que el boton desaparecia para siempre.
        setError(null);
        setColorId((current) => (p.colors.some((c) => c.id === current) ? current : (p.colors[0]?.id ?? '')));
        setSize((current) => current || (p.zones[0]?.size ?? p.variants[0]?.size ?? ''));

        // La zona y la tecnica se conservan SOLO si la nueva talla las sigue
        // admitiendo. Dejar seleccionada una tecnica que esta zona no permite
        // es lo que hacia que el boton se quedase inerte sin decir por que.
        const previa = eleccion.current;
        const zona = p.zones.find((z) => z.zoneId === previa.zoneId) ?? p.zones[0];
        setZoneId(zona?.zoneId ?? '');
        setTechnique(
          zona?.allowedTechniques.includes(previa.technique)
            ? previa.technique
            : (zona?.allowedTechniques[0] ?? ''),
        );
      })
      .catch((err) => {
        if (mia !== peticion.current) return;
        setError((err as Error).message);
      })
      .finally(() => {
        if (mia === peticion.current) setRecalculando(false);
      });
  }, [id, size]);

  // Solo mientras no hay NADA que ensenar. En cuanto la ficha existe, un error
  // posterior se muestra dentro de la pagina, sin borrar lo que el cliente ya
  // estaba mirando.
  if (!product) {
    return error ? (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <Banner tone="error" title={t('app.error')}>
          {error}
        </Banner>
      </div>
    ) : (
      <Spinner label={t('app.loading')} />
    );
  }

  const zone = product.zones.find((z) => z.zoneId === zoneId) ?? product.zones[0];
  const sizes = [...new Set(product.variants.map((v) => v.size))];

  /**
   * Empezar a diseñar.
   *
   * Sin sesión NO se manda al cliente a identificarse: se abre una de invitado
   * al vuelo. Quien viene a curiosear se iba antes de ver la herramienta, que
   * es lo único que le convencería. La cuenta se pide al pagar, y lo empezado
   * se traspasa entonces.
   */
  const start = async () => {
    if (!user) {
      try {
        await entrarComoInvitado();
      } catch {
        // Si ni siquiera eso funciona, el acceso normal sigue estando.
        try {
          await login(`/productos/${id}`);
        } catch {
          navigate(`/entrar?next=${encodeURIComponent(`/productos/${id}`)}`);
        }
        return;
      }
    }
    setCreating(true);
    try {
      const design = await api.createDesign({
        productId: product.id,
        colorId,
        viewCode: zone?.viewCode ?? 'front',
        zoneId,
        techniqueId: technique as 'dtf',
      });
      navigate(`/editor/${design.id}?size=${encodeURIComponent(size)}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const colorElegido = product.colors.find((c) => c.id === colorId);
  const imagen = colorElegido?.imagesByView?.['front'] ?? product.thumbnailUrl;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <p className="text-xs text-tinta-suave">{product.code}</p>
      <h1 className="text-2xl font-semibold">{product.name}</h1>
      <p className="mt-2 text-sm text-tinta-suave">{product.description}</p>

      {product.zones.length === 0 && (
        <div className="mt-4">
          <Banner tone="warning" title={t('product.notCalibrated')}>
            {t('product.notCalibratedHelp')}
          </Banner>
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="space-y-4">
          {/* La foto cambia con el color: es la prenda que va a recibir. */}
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-papel-suave">
            {imagen ? (
              <img
                data-test="foto"
                src={imagen}
                alt={`${product.name} · ${colorElegido?.name ?? ''}`}
                className="h-full w-full object-contain"
              />
            ) : (
              <IconImage size={40} className="text-borde" />
            )}
          </div>

          <Field label={t('product.chooseColor')}>
            <div className="flex flex-wrap gap-2">
              {product.colors.map((color) => {
                const { r, g, b } = color.approximateRgb;
                const selected = color.id === colorId;
                return (
                  <button
                    key={color.id}
                    data-test="color"
                    onClick={() => setColorId(color.id)}
                    title={color.name}
                    aria-pressed={selected}
                    className={`toque h-11 w-11 rounded-full border-2 ${
                      selected ? 'border-marca' : 'border-borde'
                    }`}
                    style={{ backgroundColor: `rgb(${r},${g},${b})` }}
                  >
                    <span className="sr-only">{color.name}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t('product.chooseSize')} hint={t('product.zoneNote')}>
            <div className="flex flex-wrap gap-2">
              {sizes.map((s) => (
                <button
                  key={s}
                  data-test="talla"
                  aria-pressed={s === size}
                  onClick={() => setSize(s)}
                  className={`toque rounded-lg border px-3 py-2 text-sm ${
                    s === size ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('product.material')}>
            <p className="text-sm">
              {product.material || '—'} <Badge>{product.substrate}</Badge>
            </p>
          </Field>
        </Card>

        <Card className="space-y-4">
          <Field label={t('product.chooseZone')}>
            <div className="space-y-2">
              {product.zones.map((z) => (
                <button
                  key={z.zoneId}
                  data-test="zona"
                  aria-pressed={z.zoneId === zoneId}
                  onClick={() => {
                    setZoneId(z.zoneId);
                    if (!z.allowedTechniques.includes(technique)) setTechnique(z.allowedTechniques[0] ?? '');
                  }}
                  className={`toque flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                    z.zoneId === zoneId ? 'border-marca bg-red-50' : 'border-borde'
                  }`}
                >
                  <IconTarget />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{z.name}</span>
                    <span className="block text-xs tabular-nums text-tinta-suave">
                      {zoneSize(z)} · {t('product.confidence', { value: Math.round(z.confidence * 100) })}
                    </span>
                  </span>
                  {/* La PROCEDENCIA se ensena tambien al cliente: una zona
                      heredada sin revisar es informacion honesta. */}
                  <Badge tone={z.provenance === 'inherited' ? 'neutral' : z.provenance === 'adjusted' ? 'ok' : 'warning'}>
                    {t(`product.provenance.${z.provenance}`)}
                  </Badge>
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('product.chooseTechnique')}>
            <div className="flex flex-wrap gap-2">
              {(zone?.allowedTechniques ?? []).map((tech) => (
                <button
                  key={tech}
                  data-test="tecnica"
                  aria-pressed={tech === technique}
                  onClick={() => setTechnique(tech)}
                  className={`toque rounded-lg border px-3 py-2 text-sm ${
                    tech === technique ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {TECHNIQUE_NAMES[tech] ?? tech}
                </button>
              ))}
            </div>
          </Field>

          {zone && (
            <p className="flex items-center gap-2 text-xs text-tinta-suave">
              <IconRuler size={16} />
              {zoneSize(zone)} · talla {zone.size}
            </p>
          )}
        </Card>
      </div>

      {/* Hueco del alto de la barra: sin el, la barra tapa el ultimo contenido
          de la pagina. */}
      <div className="h-28" aria-hidden />

      {/*
        BARRA DE ACCION.

        La accion principal estaba al final de la pagina: en un movil quedaba a
        1.577 px con una pantalla de 844, es decir, NUNCA se veia; y al elegir
        una talla el contenido se recomponia y se movia otros 382 px. Anclada
        abajo, esta siempre a la vista y no se mueve pase lo que pase.

        `barra-accion` la apoya ENCIMA de la navegacion inferior: colocada al
        mismo nivel, la navegacion se quedaba con el toque y el cliente pulsaba
        "Catalogo" creyendo que pulsaba el boton.
      */}
      <div className="barra-accion border-t border-borde bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{product.name}</p>
            <p className="truncate text-xs text-tinta-suave">
              {[colorElegido?.name, size && `${t('product.chooseSize')} ${size}`, zone?.name]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <Button
            data-test="empezar"
            onClick={() => void start()}
            loading={creating || recalculando}
            disabled={!zoneId || !technique || !colorId}
          >
            {t('catalog.personalize')}
          </Button>
        </div>
        {/* Un fallo posterior se cuenta AQUI, sin borrar la ficha. */}
        {error && (
          <p className="bg-error px-4 py-2 text-center text-xs text-white" role="alert">
            {error}
          </p>
        )}
        {product.zones.length === 0 && (
          <p className="px-4 pb-2 text-center text-xs text-aviso">{t('product.notCalibrated')}</p>
        )}
      </div>
    </div>
  );
}

function zoneSize(zone: Zone): string {
  const xs = zone.polygonMm.map((p) => p.x);
  const ys = zone.polygonMm.map((p) => p.y);
  const w = Math.round(Math.max(...xs) - Math.min(...xs));
  const h = Math.round(Math.max(...ys) - Math.min(...ys));
  return `${w} × ${h} mm`;
}
